/**
 * Hold back trailing incomplete sco-question / sco-citation markup so rehype-raw
 * never mounts half widgets while the assistant body is still streaming.
 *
 * Mirrors startups3 ChatEmbedSanitizer partial holdback:
 *  1. Incomplete open tag (no closing `>` yet)
 *  2. Complete open tag without matching close (and not self-closing)
 *
 * Complete closed embeds (and self-closing tags) are left intact.
 */
export function stripIncompleteScoEmbeds(markdown: string | null | undefined): string {
  if (!markdown) {
    return markdown || ''
  }

  let text = markdown

  // 1) Trailing incomplete open tag — no `>` after last `<`
  const lt = text.lastIndexOf('<')
  if (lt !== -1) {
    const tail = text.slice(lt)
    if (!tail.includes('>') && (/^<\/?[a-zA-Z]/.test(tail) || tail === '<')) {
      text = text.slice(0, lt)
    }
  }

  // 2) Unclosed sco-question / sco-citation (rightmost incomplete wins)
  const lower = text.toLowerCase()
  const qOpen = findLastOpenEmbedIndex(lower, 'sco-question')
  const cOpen = findLastOpenEmbedIndex(lower, 'sco-citation')
  const openIdx = Math.max(qOpen, cOpen)
  if (openIdx === -1) {
    return text
  }

  const tagName = qOpen === openIdx ? 'sco-question' : 'sco-citation'
  const closeTag = `</${tagName}>`
  const gt = text.indexOf('>', openIdx)
  if (gt === -1) {
    return text.slice(0, openIdx)
  }

  // Self-closing complete open: <sco-question ... />
  const openSlice = text.slice(openIdx, gt + 1)
  if (/\/\s*>$/.test(openSlice)) {
    return text
  }

  const closeIdx = lower.indexOf(closeTag, gt + 1)
  if (closeIdx === -1) {
    return text.slice(0, openIdx)
  }

  return text
}

function findLastOpenEmbedIndex(lower: string, tagName: string): number {
  const needle = `<${tagName}`
  let idx = -1
  let from = 0
  while (from < lower.length) {
    const found = lower.indexOf(needle, from)
    if (found === -1) {
      break
    }
    // Ensure tag boundary (not a longer unknown tag prefix)
    const next = lower[found + needle.length]
    if (next === undefined || next === '>' || next === '/' || /\s/.test(next)) {
      idx = found
    }
    from = found + needle.length
  }
  return idx
}
